import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    CircularProgress,
    Chip
} from '@mui/material';
import HistoryIcon from '@mui/icons-material/History';
import Axios from 'axios';
import { getAuthHeader } from '../../utils/authToken';
import dayjs from 'dayjs';

const GlobalCompetitionUsageModal = ({ open, onClose }) => {
    const [loading, setLoading] = useState(false);
    const [competitions, setCompetitions] = useState([]);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (open) {
            fetchCompetitionHistory();
        }
    }, [open]);

    const fetchCompetitionHistory = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await Axios.get(
                `${process.env.REACT_APP_URL}/api/v1/newsletters/competition-usage-history`,
                { headers: { Authorization: getAuthHeader() } }
            );
            setCompetitions(response.data.competitions || []);
        } catch (err) {
            console.error('Failed to fetch competition history:', err);
            setError(err.response?.data?.error || 'Failed to load competition history');
        } finally {
            setLoading(false);
        }
    };

    // Check if competition is within 4-month exclusion window
    const isWithinExclusion = (usedAt) => {
        if (!usedAt) return false;
        const fourMonthsAgo = new Date();
        fourMonthsAgo.setMonth(fourMonthsAgo.getMonth() - 4);
        return new Date(usedAt) > fourMonthsAgo;
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <HistoryIcon />
                    Competition Usage History
                </Box>
            </DialogTitle>

            <DialogContent>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress />
                    </Box>
                ) : error ? (
                    <Box sx={{ p: 2, textAlign: 'center' }}>
                        <Typography color="error">{error}</Typography>
                    </Box>
                ) : competitions.length === 0 ? (
                    <Box sx={{ p: 2, textAlign: 'center' }}>
                        <Typography color="text.secondary">
                            No competitions have been used in newsletters yet.
                        </Typography>
                    </Box>
                ) : (
                    <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Competition Name</TableCell>
                                    <TableCell>Last Used</TableCell>
                                    <TableCell>Status</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {competitions.map((comp) => {
                                    const excluded = isWithinExclusion(comp.used_at);
                                    return (
                                        <TableRow key={comp.id}>
                                            <TableCell>
                                                <Typography variant="body2">
                                                    {comp.name}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                {dayjs(comp.used_at).format('MMM D, YYYY')}
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={excluded ? 'Excluded' : 'Available'}
                                                    size="small"
                                                    color={excluded ? 'warning' : 'success'}
                                                />
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}

                {!loading && competitions.length > 0 && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
                        Total: {competitions.length} competition(s) used in newsletters.
                        Competitions marked "Excluded" are within the 4-month reuse window.
                    </Typography>
                )}
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose} variant="contained">
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default GlobalCompetitionUsageModal;
